
import React, { useState } from "react";
import {
    Button,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    SplitButton,
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    Text,
    makeStyles,
    tokens,
} from "@fluentui/react-components";

import {
    PhoneRegular,
    ArrowSyncRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
        marginTop: "12px",
    },

    optionCard: {
        cursor: "pointer",
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        transition: "all 0.2s ease",

        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            transform: "translateY(-2px)",
        },
    },

    cardBody: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        alignItems: "center",
        textAlign: "center",
        padding: "12px",
    },

    icon: {
        fontSize: "28px",
        color: tokens.colorBrandForeground1,
    },

    description: {
        color: tokens.colorNeutralForeground3,
    },
});

export default function SalesforceConnectButton({ children }) {
    const styles = useStyles();

    const [open, setOpen] = useState(false);

    // prod | sandbox
    const [selectedEnv, setSelectedEnv] = useState("prod");

    const isSandbox = selectedEnv === "sandbox";

    const handleConnect = (type) => {
        window.location.href =
            `/admin/sfRedirect?sfEnv=${selectedEnv}&type=${type}`;
    };

    const openDialog = (env) => {
        setSelectedEnv(env);
        setOpen(true);
    };

    const renderConnectionDialog = () => (
        <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <DialogSurface style={{ width: "700px", maxWidth: "95vw" }}>
                <DialogBody>
                    <DialogTitle>
                        {isSandbox
                            ? "Connect Salesforce Sandbox"
                            : "Connect Salesforce Production"}
                    </DialogTitle>

                    <DialogContent>
                        <Text>
                            Select how you want to connect Salesforce for this
                            environment.
                        </Text>

                        <div className={styles.optionContainer}>
                            {/* SCV */}
                            <Card
                                className={styles.optionCard}
                                onClick={() => handleConnect("scv")}
                            >
                                <div className={styles.cardBody}>
                                    <PhoneRegular className={styles.icon} />

                                    <Text weight="semibold" size={400}>
                                        Salesforce Voice (SCV) Connection
                                    </Text>

                                    <Text className={styles.description}>
                                        Connect Salesforce for Salesforce Voice (SCV) and calling
                                        experience.
                                    </Text>
                                </div>
                            </Card>

                            {/* SYNC */}
                            <Card
                                className={styles.optionCard}
                                onClick={() => handleConnect("sync")}
                            >
                                <div className={styles.cardBody}>
                                    <ArrowSyncRegular className={styles.icon} />

                                    <Text weight="semibold" size={400}>
                                        Sync Connection
                                    </Text>

                                    <Text className={styles.description}>
                                        Connect Salesforce for background sync and
                                        integration.
                                    </Text>
                                </div>
                            </Card>
                        </div>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            appearance="secondary"
                            onClick={() => setOpen(false)}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );

    // Custom icon trigger
    if (children) {
        return (
            <>
                <Menu positioning="below-end">
                    <MenuTrigger disableButtonEnhancement>
                        {(triggerProps) => (
                            <div
                                {...triggerProps}
                                style={{
                                    display: "inline-flex",
                                    cursor: "pointer",
                                }}
                            >
                                {children}
                            </div>
                        )}
                    </MenuTrigger>

                    <MenuPopover>
                        <MenuList>
                            <MenuItem onClick={() => openDialog("prod")}>
                                Connect Production
                            </MenuItem>

                            <MenuItem onClick={() => openDialog("sandbox")}>
                                Connect Sandbox
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>

                {renderConnectionDialog()}
            </>
        );
    }

    // Default button
    return (
        <>
            <Menu positioning="below-end">
                <MenuTrigger disableButtonEnhancement>
                    {(triggerProps) => (
                        <SplitButton
                            appearance="primary"
                            menuButton={triggerProps}
                            primaryActionButton={{
                                onClick: () => openDialog("prod"),
                            }}
                        >
                            Connect Salesforce
                        </SplitButton>
                    )}
                </MenuTrigger>

                <MenuPopover>
                    <MenuList>
                        <MenuItem onClick={() => openDialog("sandbox")}>
                            Connect Sandbox
                        </MenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>

            {renderConnectionDialog()}
        </>
    );
}